import { createTheme } from "@mui/material";

import styles from "../global.scss";

export const theme = createTheme({
    typography: {
        fontFamily: "urbanist",
        h1: {
            fontWeight: 900,
            fontSize: "108px",
            color: styles["primary_light"]
        },
        h2: {
            fontWeight: 800,
            fontSize: "64px",
            color: styles["primary_light"]
        },
        h3: {
            fontWeight: 700,
            fontSize: "48px",
            color: styles["primary_light"]
        },
        h4: {
            fontWeight: 600,
            fontSize: "32px",
            color: styles["primary_light"]
        },
        h5: {
            fontWeight: 600,
            fontSize: "26px",
            color: styles["primary_light"]
        },
        h6: {
            fontWeight: 600,
            fontSize: "22px",
            color: styles["primary_light"]
        },
        subtitle1: {
            fontWeight: 600,
            fontSize: "18px",
            color: styles["primary_light"]
        },
        subtitle2: {
            fontWeight: 600,
            fontSize: "16px",
            color: styles["primary_light"]
        },
        body1: {
            fontWeight: 500,
            fontSize: "15px", 
            color: styles["primary_light"]
        },
        body2: {
            fontWeight: 500,
            fontSize: "14px",
            color: styles["primary_light"]
        },
        button: {
            fontWeight: 700,
            fontSize: "15px",
            color: styles["primary_light"]
        },
        caption: {
            fontWeight: 500,
            fontSize: "13px",
            color: styles["primary_light"]
        },
        sm_caption: {
            fontWeight: 500,
            fontSize: "12px",
            color: styles["primary_light"]
        },
        xs_caption: {
            fontWeight: 400,
            fontSize: "11px",
            color: styles["primary_light"]
        }
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: "12px",
                    minWidth: "120px",
                    minHeight: "38.56px"
                },
                text: {
                    backgroundColor: "#00000000",
                    color: styles["primary"],
                    minWidth: "120px",
                    "&:hover": {
                        backgroundColor: styles["primary_light"],
                        color: styles["secondary"],
                    }
                },
                contained: {
                    backgroundColor: styles["secondary_light"],
                    color: styles["primary_light"],
                    "&:hover": {
                        backgroundColor: styles["primary"],
                        color: styles["secondary"],
                    }
                },
                outlined: {
                    borderColor: styles["primary_light"],
                    color: styles["primary_light"],
                    "&:hover": {
                        backgroundColor: styles["primary"],
                        borderColor: styles["secondary"],
                        color: styles["secondary"],
                    }
                },
                containedError: {
                    backgroundColor: "#d32f2f",
                    color: styles["primary_light"],
                    "&:hover": {
                        backgroundColor: styles["primary"],
                        color: styles["secondary"],
                    }
                }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: "12px",
                    borderColor: "red",
                    color: styles["primary_light"],
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: "#676767",
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: styles["secondary_light"],
                    },
                    // No palette block is declared, so MUI falls back to its
                    // LIGHT-mode disabled colour — rgba(0,0,0,0.38) — which is
                    // effectively black on this app's dark surfaces and made
                    // disabled fields look empty rather than disabled.
                    '&.Mui-disabled': {
                        color: "rgba(226, 242, 249, 0.45)",
                        WebkitTextFillColor: "rgba(226, 242, 249, 0.45)",
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: "#2c2d2f",
                        },
                    },
                },
                input: {
                    '&.Mui-disabled': {
                        WebkitTextFillColor: "rgba(226, 242, 249, 0.45)",
                    },
                },
                notchedOutline: {
                    borderColor: "#343434",
                },
            }
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    color: "grey",
                    // same reason as above — the default disabled label is
                    // near-black and disappears against the dark background
                    '&.Mui-disabled': {
                        color: "rgba(226, 242, 249, 0.35)",
                    },
                }
            }
        }, 
        MuiBackdrop: {
            styleOverrides: {
                root: {
                    // background: "rgba( 50, 50, 50, 0.1 )",
                    // boxShadow: "0 8px 32px 0 rgba( 31, 38, 135, 0.37 )",
                    // "backdrop-filter": "blur( 6px )",
                    // "-webkit-backdrop-filter": "blur( 6px )",
                }
            }
        },
        MuiPopover: {
            styleOverrides: {
                root: {
                    borderRadius: "12px",
                    // pointerEvents: 'none',
                },
                paper: {
                    borderRadius: "12px",
                    // pointerEvents: "auto",
                },
            }
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    backgroundColor: "rgba(44, 45, 47, 0.64)",
                    boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)",
                    backdropFilter: "blur(18.3px)",
                    "-webkit-backdrop-filter": "blur(18.3px)",
                    border: "1px solid rgba(44, 45, 47, 0.72)",
                }
            }
        },
        MuiAutocomplete: {
            // The suggestion popup is portalled to the body, so none of the
            // dark .block / .modal surfaces reach it. With no override it
            // renders on MUI's default LIGHT Paper while the options inherit
            // the themed primary_light text — pale blue on white, unreadable.
            // Same surface language as MuiMenu above, just far more opaque:
            // this list floats over tables rather than over a modal backdrop.
            styleOverrides: {
                paper: {
                    backgroundColor: "rgba(44, 45, 47, 0.96)",
                    color: styles["primary_light"],
                    boxShadow: "0 4px 30px rgba(0, 0, 0, 0.35)",
                    backdropFilter: "blur(18.3px)",
                    "-webkit-backdrop-filter": "blur(18.3px)",
                    border: "1px solid rgba(44, 45, 47, 0.72)",
                    borderRadius: "12px",
                },
                listbox: {
                    "& .MuiAutocomplete-option": {
                        color: styles["primary_light"],
                        "&:hover": {
                            backgroundColor: "rgba(184, 223, 240, 0.12)",
                        },
                        // keyboard navigation highlight
                        "&.Mui-focused": {
                            backgroundColor: "rgba(184, 223, 240, 0.16)",
                        },
                        '&[aria-selected="true"]': {
                            backgroundColor: "rgba(10, 102, 194, 0.35)",
                        },
                        '&[aria-selected="true"].Mui-focused': {
                            backgroundColor: "rgba(10, 102, 194, 0.45)",
                        },
                    },
                },
                // "No options" / "Loading…" sit outside the listbox and would
                // otherwise keep MUI's light-mode secondary text colour.
                noOptions: {
                    color: "rgba(226, 242, 249, 0.6)",
                },
                loading: {
                    color: "rgba(226, 242, 249, 0.6)",
                },
                groupLabel: {
                    backgroundColor: "rgba(44, 45, 47, 0.96)",
                    color: styles["primary_dark"],
                },
                popupIndicator: {
                    color: styles["primary"],
                },
                clearIndicator: {
                    color: styles["primary"],
                },
            }
        },
        MuiTable: {
            styleOverrides: {
                root: {
                    overflow: "hidden"
                }
            }
        },
        MuiTableSortLabel: {
            // MuiButtonBase-root MuiTableSortLabel-root css-1qgma8u-MuiButtonBase-root-MuiTableSortLabel-root
            styleOverrides: {
                root: {
                    color: styles["primary_dark"],
                    "&:hover": {
                        color: "#6ec2e7"
                    },
                    '&.Mui-active': {
                        color: styles["secondary_light"]
                    },
                    "&.Mui-active .MuiTableSortLabel-icon": {
                        color: styles["secondary_light"]
                    }

                },

            }
        },
        MuiTableCell: {
            styleOverrides: {
                head: {
                    color: styles["primary_dark"],
                    borderColor: "black",
                    padding: "8px 16px",
                },
                body: {
                    color: styles["primary_light"],
                    borderColor: "black",
                    padding: "8px 16px",
                },
            }
        },
        MuiTablePagination: {
            styleOverrides: {
                select: {
                    color: styles["primary"]
                },
                selectIcon: {
                    color: styles["primary"]
                }
            }
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    fontWeight: 600,
                    fontSize: "18px"
                }
            }
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    "&:hover": {
                        backgroundColor: styles["secondary"]
                    }
                },
            }
        },
        MuiChip: {
            styleOverrides: {
                labelSmall: {
                    fontSize: "12px"
                },
                filled: {
                    backgroundColor: "#e1e1e1",
                    color: "#434343",
                    fontWeight: 500,
                    "&.MuiChip-colorError": {
                        backgroundColor: "#b24020",
                        color: "#fadfd8"
                    },
                    "&.MuiChip-colorSuccess": {
                        backgroundColor: "#44712e",
                        color: "#d7ebce"
                    },
                    "&.MuiChip-colorInfo": {
                        backgroundColor: "#004182",
                        color: "#dce6f1"
                    },
                    "&.MuiChip-colorWarning": {
                        backgroundColor: "#915907",
                        color: "#fce2ba"
                    },
                },
                outlined: {
                    fontWeight: 500,
                    "&.MuiChip-colorError": {
                        backgroundColor: "#b24020",
                        color: "#fadfd8"
                    },
                    "&.MuiChip-colorSuccess": {
                        backgroundColor: "#44712e",
                        color: "#d7ebce"
                    },
                    "&.MuiChip-colorInfo": {
                        backgroundColor: "#004182",
                        color: "#dce6f1"
                    },
                    "&.MuiChip-colorWarning": {
                        backgroundColor: "#915907",
                        color: "#fce2ba"
                    },
                },
            }
        },
        MuiSwitch: {
            styleOverrides: {
                root: {
                    padding: 6,
                },
                thumb: {
                    backgroundColor: styles["primary"],
                    boxShadow: 'none',
                    width: 18,
                    height: 18,
                    margin: 1,
                },
                track: {
                    border: "1.5px solid",
                    borderColor: styles["secondary"],
                    backgroundColor: "red",
                    borderRadius: 26 / 2,
                    '&::before, &::after': {
                        content: '""',
                        position: 'absolute',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 18,
                        height: 18,
                    },
                },
            }
        },
    }
})